import { prop, pick } from 'ramda';
import React, { useEffect, Suspense, useState, useCallback } from 'react';
import { Login } from '@core/js/components/main/Login';
import { BACKBONE_APP_ROOT } from '@core/js/backbone/constants';
import { ManagedPopup } from '@core/js/components/common/Popup';
import { scrollService } from '@core/js/services/scroll.service';
import { WebContentInner } from '@core/js/components/system/WebContent';
import { useCombinedOnClose } from '@core/js/components/common/PopupManager';
import { useAuthSelector, useAppSelector, useUserSelector, useDeviceSelector } from '@core/js/store/hooks';

// @theme
import { dispatcher } from 'dispatcher';
import { useDispatch } from 'react-redux';
import { NEVIS_ERRORS } from './constants';
import { useDTAG } from '@theme/js/hooks/useDTAG';
import { useTranslations } from '@core/js/hooks/i18n';
import { Loader } from '@core/js/components/common/Loader';
import { languageService } from '@core/js/services/language.service';
import { userSetAttributesAction } from '@core/js/hooks/user/store/actions';
import { useValidateSessionForFido } from '@theme/js/hooks/auth/hooks/useValidateSessionForFido';
import { useContinueLoginWithSessionToken } from '@core/js/hooks/auth/hooks/useContinueLoginWithSessionToken';

import * as pas from '@core/js/hooks/auth/pas';
import * as actions from '@core/js/hooks/auth/store/actions';

import style from './LoginPopup.scss';

const layerClassNames = {
    layout: style.layout
};

export const LoginPopup = ({ id, title, onClose, onLogin, controls }) => {
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const loginWebContent = useAppSelector(prop('loginWebContent'));
    const closePopup = useCombinedOnClose(onClose, id);
    const isLoginInProcess = useAuthSelector(prop('isLoginInProcess'));
    // #region @theme
    const { lang } = useUserSelector(pick(['lang']));
    const dispatch = useDispatch();
    const languageTwoLetter = languageService.getTwoLetterLanguage(lang).toUpperCase();
    const isFidoSupported = window.fidoAuth?.isSupported();
    const continueLoginWithSessionToken = useContinueLoginWithSessionToken();
    const validateSession = useValidateSessionForFido();
    const { isMobile, isTablet, os } = useDeviceSelector(pick(['isMobile', 'isTablet', 'os']));
    const [loading, setLoading] = useState(false);
    const [runEffect, setRunEffect] = useState(true);
    const isFidoEnabled = useDTAG('DTAG_ENABLE_FIDO_AUTH', Number) === 1;
    const inputFields = window.document.querySelectorAll('.username');
    const t = useTranslations();
    const isIOS = os === 'ios';

    const updateMargin = useCallback((layoutElement, targetMargin, forceSet = false) => {
        if (forceSet) {
            layoutElement.style.marginBottom = `${targetMargin}px`;
            return;
        }

        const currentMargin = parseFloat(layoutElement.style.marginBottom) || 0;
        const difference = targetMargin - currentMargin;
        const step = difference > 0 ? Math.ceil(difference / 10) : Math.floor(difference / 10); // Adjust step size as needed
        const newMargin = currentMargin / 1.2 + step;

        layoutElement.style.marginBottom = `${newMargin}px`;

        // If margin hasn't reached the target yet, request another animation frame
        if (step > 0 && newMargin < targetMargin || step < 0 && newMargin > targetMargin) {
            requestAnimationFrame(updateMargin);
        }
    }, []);

    const handleKeyboardVisibility = useCallback((forceSetMargin = false) => {
        const layoutElement = window.document.querySelector(`.${style.layout}`);
        if (layoutElement) {
            const windowHeight = window.innerHeight;
            const layoutHeight = layoutElement.offsetHeight;
            const targetMargin = (windowHeight - layoutHeight) / 2;

            updateMargin(layoutElement, targetMargin, forceSetMargin);
        }
    }, [updateMargin]);

    const handleKeyboardVisibilityForMobile = useCallback(() => handleKeyboardVisibility(true), [handleKeyboardVisibility]);

    const handleFocus = useCallback((event) => {
        if (inputFields && inputFields.length && Array.from(inputFields).includes(event.target)) {
            handleKeyboardVisibility();
        }
    }, [handleKeyboardVisibility, inputFields]);

    const handleBlur = useCallback(() => {
        // Optionally, you can reset the layout when an input field loses focus
    }, []);

    useEffect(() => {
        if (isIOS) {
            setTimeout(() => {
                window.addEventListener('focus', handleFocus, true); // Capture phase to ensure focus event is caught
                window.addEventListener('blur', handleBlur, true); // Capture phase to ensure blur event is caught

                // Cleanup event listeners
                return () => {
                    window.removeEventListener('focus', handleFocus, true);
                    window.removeEventListener('blur', handleBlur, true);
                };
            }, 0);
        } else {
            if (isMobile || isTablet) {
                // Add event listeners to detect keyboard visibility
                window.addEventListener('resize', () => handleKeyboardVisibilityForMobile);

                return () => {
                    // Clean up event listeners on component unmount
                    window.removeEventListener('resize', handleKeyboardVisibilityForMobile);
                };
            }
        }
        // eslint-disable-next-line
    }, [isMobile, isTablet]);

    const handleLoginError = useCallback((response, error, overlay) => {
        // If we're having Nevis error that isn't related to "Authentificator isn't found", we won't count it as error.
        if (!error && !Object.values(NEVIS_ERRORS).includes(response.errorCode)) {
            return;
        }

        dispatcher.dispatch('auth:credentials:login:failed', 'nevis');
        const messElement = window.document.getElementsByClassName('form')
            && window.document.getElementsByClassName('form')[0]
            && window.document.getElementsByClassName('form')[0]?.getElementsByClassName('fn-form-messages')[0];
        const errorMessageElement = window.document.createElement('p');
        let message = error?.data?.playerMessage || 'Login error';

        if (!error) {
            message = t(`playtech.mobile.system.login.fido.${response.errorCode}`);
        }

        if (error && error?.data?.actions &&
            error?.data?.actions?.PlayerActionShowMessage &&
            error?.data?.actions?.PlayerActionShowMessage?.length > 0) {
            message = error?.data?.actions?.PlayerActionShowMessage[0]?.message;
        }

        errorMessageElement.className = 'message error';
        errorMessageElement.textContent = message;
        messElement && messElement.appendChild(errorMessageElement);

        console.error('error: ', error, response);

        setLoading(false); // Stop loading

        if (overlay && overlay[0]) {
            overlay[0].style.zIndex = '0'; // Remove overlay
        }
    }, [t]);

    const authenticateCallback = useCallback(async (response) => {
        const overlay = window.document.getElementsByClassName('react-overlay');

        if (response.errorCode === window.iapiERR_OK) {
            const options = {
                loginMode: 'fidoAuthUi',
                username: response.username,
                languageTwoLetter
            };

            const initialCredentials = {
                userName: response.username,
                password: response.password,
                loginMode: 'fidoAuthUi',
                languageTwoLetter
            };

            try {
                setLoading(true); // Start loading

                if (overlay && overlay[0]) {
                    overlay[0].style.zIndex = '100'; // Apply overlay for loading
                }

                const pasLoginResponse = await pas.login(options, response, 1);

                if (!validateSession(pasLoginResponse, initialCredentials, { isAfterLoginWithCredentials: true })) {
                    dispatch(actions.setIsLoginInProcess(false));
                    dispatch(userSetAttributesAction({
                        relogin: true
                    }));

                    setLoading(false); // Stop loading

                    if (overlay && overlay[0]) {
                        overlay[0].style.zIndex = '0'; // Remove overlay
                    }

                    return Promise.reject(pasLoginResponse);
                }
            } catch (error) {
                handleLoginError(response, error, overlay);
            }

            const tempTokenResponse = await pas.getTempToken();
            const sessionToken = tempTokenResponse?.data?.sessionToken?.sessionToken;
            await continueLoginWithSessionToken({
                userId: response.userId,
                userName: response.username,
                sessionToken,
                afterRegisterLogin: false,
                isAfterLoginWithCredentials: false
            });

            if (overlay && overlay[0]) {
                overlay[0].style.zIndex = '0'; // Remove overlay
            }

            dispatcher.dispatch('auth:credentials:login', 'nevis');
        } else {
            handleLoginError(response, null, overlay);
        }
    }, [dispatch, languageTwoLetter, validateSession, continueLoginWithSessionToken, handleLoginError]);
    // #endregion

    useEffect(() => {
        if (!isLoggedIn || isLoginInProcess) {
            return;
        }

        if (onLogin) {
            onLogin();
        }

        closePopup();
        scrollService.scrollTo(0);
    }, [closePopup, isLoggedIn, onLogin, isLoginInProcess]);

    // #region @theme
    useEffect(() => {
        if (!runEffect || !isFidoEnabled) {
            return; // Skip running the effect if runEffect is false
        }

        const isHasPasskeys = window.localStorage.getItem('hasPasskeys');

        window.fidoAuth?.abortCurrent();

        if (isFidoSupported) {
            if (isHasPasskeys === 'true') {
                window.fidoAuth.authenticate(authenticateCallback)
                    .catch();
            } else {
                window.fidoAuth.authenticateAutofillUI(authenticateCallback)
                    .catch();
            }
        }

        // Reset runEffect to false after the effect has run
        setRunEffect(false);
        // eslint-disable-next-line
    }, [isFidoSupported]);
    // #endregion

    return (
        <ManagedPopup
            id={id}
            title={title}
            classNames={style}
            controls={controls}
            onClose={closePopup}
            className={style.container}
            layerClassNames={layerClassNames}
        >
            {/* #region @theme */}
            {loading && (
                <div className={style.loginLoader}>
                    <Loader></Loader>
                </div>
            )}
            {/* #endregion */}
            <Suspense fallback="">
                <Login
                    className={`${style.loginPopupContainer} ${BACKBONE_APP_ROOT} ${loading ? style.noEvents : ''}`} // @theme
                />
            </Suspense>
            {loginWebContent &&
                <WebContentInner
                    popupId={id}
                    content={loginWebContent}
                    className={`${style.loginWc} ${BACKBONE_APP_ROOT} ${loading ? style.noEvents : ''}`} // @theme
                />
            }
        </ManagedPopup>
    );
};
